export const logAndroidDeviceInfo = async () => {
  const userAgent = navigator.userAgent;
  const isAndroid = /Android/i.test(userAgent);

  if (!isAndroid) {
    return;
  }

  let model = 'Unknown';
  let manufacturer = 'Unknown';
  let androidVersion = 'Unknown';

  // Chrome 90+ exposes userAgentData with high-entropy values (actual model etc.)
  if (navigator.userAgentData) {
    try {
      const ua = await navigator.userAgentData.getHighEntropyValues([
        'model',
        'platformVersion',
        'fullVersionList',
      ]);
      model = ua.model || 'Unknown';
      androidVersion = ua.platformVersion || 'Unknown';
    } catch (_) { /* fall through to UA parsing */ }
  }

  if (model === 'Unknown') {
    const parenthesisMatch = userAgent.match(/\(([^)]*)\)/);
    if (parenthesisMatch) {
      const segments = parenthesisMatch[1].split(';').map(s => s.trim());
      const modelSegment = segments.find(s => /Build\//i.test(s)) || segments[segments.length - 1];
      model = modelSegment.replace(/\s*Build\/.*$/i, '').trim() || 'Unknown';
    }
  }

  if (androidVersion === 'Unknown') {
    const versionMatch = userAgent.match(/Android\s+([\d.]+)/i);
    androidVersion = versionMatch ? versionMatch[1] : 'Unknown';
  }

  const manufacturerMap = [
    { prefixes: ['SM-', 'Galaxy', 'Samsung'], name: 'Samsung' },
    { prefixes: ['Xiaomi', 'Redmi', 'POCO', 'Mi ', 'M2', '2201', '2211', '23'], name: 'Xiaomi' },
    { prefixes: ['HUAWEI', 'HW-', 'VOG-', 'ELS-', 'ANA-'], name: 'Huawei' },
    { prefixes: ['OPPO', 'CPH'], name: 'OPPO' },
    { prefixes: ['vivo', 'V2'], name: 'vivo' },
    { prefixes: ['OnePlus', 'IN2', 'KB2', 'LE2', 'NE2'], name: 'OnePlus' },
    { prefixes: ['Pixel'], name: 'Google' },
    { prefixes: ['moto', 'Motorola', 'XT'], name: 'Motorola' },
    { prefixes: ['LG-', 'LM-'], name: 'LG' },
    { prefixes: ['Sony', 'Xperia', 'XQ-'], name: 'Sony' },
    { prefixes: ['RMX'], name: 'Realme' },
    { prefixes: ['Nokia'], name: 'Nokia' },
    { prefixes: ['ASUS', 'ZS', 'ZE', 'ZB'], name: 'ASUS' },
    { prefixes: ['Tecno', 'TECNO'], name: 'Tecno' },
    { prefixes: ['Infinix'], name: 'Infinix' },
    { prefixes: ['HTC'], name: 'HTC' },
  ];

  for (const entry of manufacturerMap) {
    if (entry.prefixes.some(p => model.toUpperCase().startsWith(p.toUpperCase()))) {
      manufacturer = entry.name;
      break;
    }
  }

  const deviceInfo = [
    '========== Android Device Info ==========',
    `Manufacturer: ${manufacturer}`,
    `Model: ${model}`,
    `Android Version: ${androidVersion}`,
    `User Agent: ${userAgent}`,
    '=========================================',
  ].join('\n');

  console.log(deviceInfo);
};

export const getDeviceInfo = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  const isAndroid = /android/i.test(userAgent);
  const userAgentLower = userAgent.toLowerCase();

  // Extract GPU info from user agent
  let gpuInfo = 'Unknown';
  if (userAgentLower.includes('mali')) {
    const maliMatch = userAgentLower.match(/mali[- ]?(\w+)/i);
    gpuInfo = maliMatch ? `Mali ${maliMatch[1].toUpperCase()}` : 'Mali';
  } else if (userAgentLower.includes('adreno')) {
    const adrenoMatch = userAgentLower.match(/adreno[- ]?(\d+)/i);
    gpuInfo = adrenoMatch ? `Adreno ${adrenoMatch[1]}` : 'Adreno';
  } else if (userAgentLower.includes('powervr')) {
    gpuInfo = 'PowerVR';
  }

  // Extract CPU/Processor info from user agent
  let cpuInfo = 'Unknown';
  if (userAgentLower.includes('mediatek') || userAgentLower.includes('mt')) {
    const mtMatch = userAgentLower.match(/mt(\d+)/i);
    cpuInfo = mtMatch ? `MediaTek MT${mtMatch[1]}` : 'MediaTek';
  } else if (userAgentLower.includes('helio')) {
    const helioMatch = userAgentLower.match(/helio[- ]?(\w+)/i);
    cpuInfo = helioMatch ? `MediaTek Helio ${helioMatch[1].toUpperCase()}` : 'MediaTek Helio';
  } else if (userAgentLower.includes('dimensity')) {
    const dimensityMatch = userAgentLower.match(/dimensity[- ]?(\d+)/i);
    cpuInfo = dimensityMatch ? `MediaTek Dimensity ${dimensityMatch[1]}` : 'MediaTek Dimensity';
  } else if (userAgentLower.includes('snapdragon') || userAgentLower.includes('sm')) {
    const snapMatch = userAgentLower.match(/snapdragon[- ]?(\d+)/i) || userAgentLower.match(/sm(\d+)/i);
    cpuInfo = snapMatch ? `Snapdragon ${snapMatch[1]}` : 'Snapdragon';
  } else if (userAgentLower.includes('exynos')) {
    const exynosMatch = userAgentLower.match(/exynos[- ]?(\d+)/i);
    cpuInfo = exynosMatch ? `Exynos ${exynosMatch[1]}` : 'Exynos';
  } else if (userAgentLower.includes('kirin')) {
    const kirinMatch = userAgentLower.match(/kirin[- ]?(\d+)/i);
    cpuInfo = kirinMatch ? `Kirin ${kirinMatch[1]}` : 'Kirin';
  }

  return {
    isAndroid,
    userAgent: userAgentLower,
    cores: navigator.hardwareConcurrency || 0,
    cpuInfo,
    gpuInfo
  };
};

export const shouldUseOnnxModel = () => {
  const deviceInfo = getDeviceInfo();
  const { userAgent, cores, isAndroid } = deviceInfo;

  // Check device type
  const isIOS = /iphone|ipad|ipod/i.test(userAgent);
  const isDesktop = !isAndroid && !isIOS;

  // Use ONNX model only for Android devices
  const shouldUseOnnx = isAndroid;

  // Check for Mali GPU (ARM Mali graphics)
  const hasMaliGPU = userAgent.includes('mali');

  // Check for MediaTek processor (MT, Helio, Dimensity series)
  const hasMediaTek = userAgent.includes('mt') ||
    userAgent.includes('mediatek') ||
    userAgent.includes('helio') ||
    userAgent.includes('dimensity');

  // Check if cores are 4 or less
  const hasLowCoreCount = cores > 0 && cores <= 4;

  // Log for debugging
  console.log('Device Detection:', {
    isAndroid,
    isIOS,
    isDesktop,
    cpuInfo: deviceInfo.cpuInfo,
    gpuInfo: deviceInfo.gpuInfo,
    hasMaliGPU,
    hasMediaTek,
    cores,
    hasLowCoreCount,
    shouldUseOnnx,
    usingModel: shouldUseOnnx ? 'ONNX Model (TakeAPhotoWithOnnx)' : 'TensorFlow.js (TakeAPhoto)'
  });

  // Use ONNX model only for Android devices
  return shouldUseOnnx;
};

